import type { NoticeBoardItem } from "../../types/noticeboard";

const PRIORITY_COLOURS = {
  red: { border: "border-red-400", bg: "bg-red-50", hover: "hover:bg-red-100", title: "text-red-800", desc: "text-red-700", badge: "bg-red-100 text-red-700" },
  amber: { border: "border-amber-400", bg: "bg-amber-50", hover: "hover:bg-amber-100", title: "text-amber-800", desc: "text-amber-700", badge: "bg-amber-100 text-amber-700" },
  green: { border: "border-emerald-400", bg: "bg-emerald-50", hover: "hover:bg-emerald-100", title: "text-emerald-800", desc: "text-emerald-700", badge: "bg-emerald-100 text-emerald-700" },
  neutral: { border: "border-neutral-300", bg: "bg-neutral-50", hover: "hover:bg-neutral-100", title: "text-neutral-700", desc: "text-neutral-500", badge: "bg-neutral-100 text-neutral-500" },
};

function formatCountdown(days: number | null | undefined): string {
  if (days == null) return "";
  if (days < 0) return `Overdue ${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""}`;
  if (days === 0) return "Today";
  if (days <= 30) return `${days} day${days !== 1 ? "s" : ""}`;
  if (days <= 365) return `${Math.ceil(days / 30)} month${Math.ceil(days / 30) !== 1 ? "s" : ""}`;
  return `${Math.round(days / 365)} yr`;
}

export interface NoticeItemProps {
  item: NoticeBoardItem;
  compact?: boolean;
  onNavigate?: (page: string) => void;
  onEmail?: (item: NoticeBoardItem) => void;
  onSelectProperty?: (id: string) => void;
}

export function NoticeItem({ item, compact, onNavigate, onEmail, onSelectProperty }: NoticeItemProps) {
  const c = PRIORITY_COLOURS[item.priority];
  const countdown = formatCountdown(item.daysUntil);

  const handleRowClick = () => {
    if (item.propertyId && onSelectProperty) {
      onSelectProperty(item.propertyId);
      return;
    }
    if (item.page && onNavigate) {
      onNavigate(item.page);
    }
  };

  const isClickable = !compact && ((!!item.propertyId && !!onSelectProperty) || (!!item.page && !!onNavigate));

  return (
    <div
      className={`rounded-lg border border-l-4 ${c.border} ${c.bg} ${compact ? "px-3 py-2" : "p-4"} flex items-start gap-3 shadow-sm ring-1 ring-black/5 transition-all ${isClickable ? `${c.hover} cursor-pointer hover:shadow-md hover:-translate-y-0.5` : ""}`}
      onClick={isClickable ? handleRowClick : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleRowClick(); } } : undefined}
    >
      <div className="flex-1 min-w-0">
        <div className={`${compact ? "text-xs" : "text-sm"} font-medium ${c.title}`}>{item.title}</div>
        <div className={`text-xs mt-0.5 ${c.desc} ${compact ? "truncate" : ""}`}>{item.description}</div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        {countdown && (
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${c.badge}`}>{countdown}</span>
        )}
        {item.emailAction && onEmail && (
          <button onClick={(e) => { e.stopPropagation(); onEmail(item); }} className="inline-flex items-center gap-0.5 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-100">
            {compact ? "✉" : "✉ Email"}
          </button>
        )}
        {!compact && item.category && (
          <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] text-neutral-500">{item.category.replace(/-/g, " ")}</span>
        )}
        {!compact && isClickable && (
          <span className="text-xs text-neutral-400">→</span>
        )}
      </div>
    </div>
  );
}

export function NoticeTier({ label, items, colour, onNavigate, onEmail, onSelectProperty }: {
  label: string;
  items: NoticeBoardItem[];
  colour: "red" | "amber" | "green" | "neutral";
  onNavigate: (page: string) => void;
  onEmail?: (item: NoticeBoardItem) => void;
  onSelectProperty?: (id: string) => void;
}) {
  const colourMap = { red: "text-red-700", amber: "text-amber-700", green: "text-emerald-700", neutral: "text-neutral-500" };
  return (
    <div>
      <div className="flex items-center gap-2 mb-1.5">
        <span className={`text-xs font-semibold uppercase tracking-wider ${colourMap[colour]}`}>{label}</span>
        <span className="text-[10px] text-neutral-400">{items.length}</span>
      </div>
      <div className="space-y-1.5">
        {items.map((item) => <NoticeItem key={item.id} item={item} onNavigate={onNavigate} onEmail={onEmail} onSelectProperty={onSelectProperty} />)}
      </div>
    </div>
  );
}
